<template>
  <div class="interaction-preview">
    <div v-if="renderComponent" class="interaction-preview-card">
      <!-- :key=renderEpoch：Vue2 对同构造器就地 patch 不重建实例，渲染器 data() 只在创建时
           从 session 播种——重 init（审校改题重预览）若不换 key，旧作答状态会残留到新题上 -->
      <component
        :is="renderComponent"
        :key="renderEpoch"
        v-bind="rendererProps"
        @session-changed="handleSessionChanged"
      />
    </div>
    <div v-else class="interaction-preview-empty">
      {{ errorMessage || "等待题目数据…" }}
    </div>
  </div>
</template>

<script>
// 宿主页零 fetch 零登录态零 Vuex：数据全经 postMessage 协议获取（见 @saas/interactions/src/protocol.js）。
// 学生正式播放不走此页，仅供 Tier 1 iframe 预览 / admin-web 审校 / 未来 Tier 2 WebView 沙箱嵌入。
import {
  getRenderer,
  parseInit,
  buildReply,
  MSG_TYPES,
} from "@saas/interactions";

// 只有 ChoiceRenderer 类多题型合一组件额外接收 typeKey（契约 src/contract.md）
const CHOICE_TYPE_KEYS = ["single_choice", "multi_choice", "judgment"];

export default {
  name: "InteractionPreview",
  data() {
    return {
      typeKey: "",
      view: null,
      session: null,
      env: { mode: "answer" },
      feedback: null,
      errorMessage: "",
      // 通过白名单校验后记住的唯一出站 targetOrigin；收到合法 init 前保持空，不出站敏感消息
      parentOrigin: "",
      allowedOrigins: this.buildAllowedOrigins(),
      // 每次接受合法 init 递增，作为 <component :key> 强制销毁重建渲染器实例
      renderEpoch: 0,
    };
  },
  computed: {
    renderComponent() {
      return this.typeKey ? getRenderer(this.typeKey) : null;
    },
    rendererProps() {
      const props = {
        view: this.view,
        session: this.session,
        env: this.env,
        feedback: this.feedback,
      };
      if (CHOICE_TYPE_KEYS.includes(this.typeKey)) {
        props.typeKey = this.typeKey;
      }
      return props;
    },
  },
  mounted() {
    window.addEventListener("message", this.handleMessage);
    // ready 不含任何题面/答案数据，此时也尚未收到合法 init、无法得知宿主 origin，targetOrigin 用 '*' 无泄露风险
    window.parent.postMessage(buildReply(MSG_TYPES.READY), "*");
  },
  beforeDestroy() {
    window.removeEventListener("message", this.handleMessage);
  },
  errorCaptured(err) {
    this.typeKey = "";
    this.errorMessage = `渲染异常：${
      err && err.message ? err.message : String(err)
    }`;
    this.postToParent(
      buildReply(MSG_TYPES.ERROR, { message: this.errorMessage })
    );
    return false;
  },
  methods: {
    buildAllowedOrigins() {
      const configured = (process.env.VUE_APP_INTERACTION_PREVIEW_ORIGINS || "")
        .split(",")
        .map((origin) => origin.trim())
        .filter(Boolean);
      return [...new Set([...configured, window.location.origin])];
    },
    // 支持"改完重预览"：审校场景下同 origin 可重复发送 init，覆盖当前渲染
    handleMessage(event) {
      // 防御纵深：只认嵌入方（window.parent）为指令来源。origin 白名单挡跨站，
      // source 校验再挡"同 origin 的其他窗口/iframe/页内脚本伪造 init"——Tier 2/WebView
      // 宿主复用此页时尤其重要（WebView 注入桥同样以 parent/host 为唯一合法通道）。
      // 独立打开本页（未嵌 iframe）时 window.parent === window，自发消息不受影响。
      if (event.source !== window.parent) {
        return;
      }
      const result = parseInit(event, this.allowedOrigins);
      if (!result.ok) {
        // 非白名单 origin / 非 init 类型 / 缺字段：静默丢弃（页面间常见噪声消息），不上报错误
        return;
      }
      this.parentOrigin = event.origin;
      // 递增 epoch：配合 <component :key> 强制销毁重建，杜绝重 init 状态残留
      this.renderEpoch += 1;
      const { typeKey, view, env, feedback, session } = result.payload;
      if (!getRenderer(typeKey)) {
        this.typeKey = "";
        this.errorMessage = `未知题型：${typeKey}`;
        this.postToParent(
          buildReply(MSG_TYPES.ERROR, { message: this.errorMessage })
        );
        return;
      }
      this.errorMessage = "";
      this.typeKey = typeKey;
      this.view = view;
      this.env = env || { mode: "answer" };
      this.feedback = feedback || null;
      this.session = session || null;
    },
    handleSessionChanged(payload) {
      this.postToParent(buildReply(MSG_TYPES.SESSION_CHANGED, payload));
    },
    postToParent(message) {
      if (!this.parentOrigin) return;
      window.parent.postMessage(message, this.parentOrigin);
    },
  },
};
</script>

<style src="@saas/interactions/src/styles/base.css"></style>

<style scoped>
.interaction-preview {
  box-sizing: border-box;
  min-height: 100vh;
  background: #ffffff;
  padding: 24px 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  justify-content: center;
}

.interaction-preview-card {
  width: 100%;
  max-width: 640px;
}

.interaction-preview-empty {
  width: 100%;
  max-width: 640px;
  margin-top: 40px;
  text-align: center;
  color: #909399;
  font-size: 14px;
  line-height: 1.6;
}
</style>
